import {
  CellClassParams,
  CellClickedEvent,
  ICellRendererParams,
  ISetFilterParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { isNumber } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';

import { AgIconLinkCellComponent } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.component';
import { AgIconLinkCellComponentParams } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.model';
import { AgTooltipComponent } from '@shared/components/ag-tooltip/ag-tooltip.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { AuxExcelStyleKeys, isRoundedToZero } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { MapPurchaseOrderStatus } from './purchase-orders.model';

// eslint-disable-next-line max-lines-per-function
export const PurchaseOrdersColumns = ({
  attributesModalNavigate,
  budgetNavigate,
  hasBudgetEditPermission,
  invoicesNavigate,
  isChangeOrdersWorkflowLocked,
  isWarningsVisible$,
}: {
  attributesModalNavigate: (params: AgIconLinkCellComponentParams) => void;
  budgetNavigate: (params: AgIconLinkCellComponentParams) => void;
  hasBudgetEditPermission: boolean;
  invoicesNavigate: (event: CellClickedEvent) => void;
  isChangeOrdersWorkflowLocked: boolean;
  isWarningsVisible$: BehaviorSubject<boolean>;
}) => {
  return [
    {
      field: 'po_id',
      headerName: 'ID',
      hide: true,
    },
    {
      children: [
        {
          cellClass: ['!block', 'truncate', 'text-left'],
          cellRenderer: AgIconLinkCellComponent,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              icons: [
                {
                  icon: 'AlertTriangle',
                  iconClass: 'text-aux-red-dark',
                  iconTooltipMessages: [MessagesConstants.PO.MISSING_VENDOR],
                  showIcon:
                    isWarningsVisible$.value &&
                    !params.data.organization_id &&
                    !params.node.rowPinned,
                },
              ],
              rowReverse: true,
            };
          },
          comparator: (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()),
          field: 'organization_name',
          headerClass: ['ag-header-align-center'],
          headerName: 'Vendor Contract',
          minWidth: 200,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) => {
            if (params.node?.isRowPinned()) {
              return 'Total';
            }

            return Utils.dashFormatter(params);
          },
        },
        {
          cellClass: ['!block', 'truncate', 'text-left', '!leading-[40px]'],
          cellRenderer: AgTooltipComponent,
          comparator: (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()),
          field: 'po_number',
          headerClass: ['ag-header-align-center'],
          headerName: 'PO #',
          minWidth: 180,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (val: ValueFormatterParams) => {
            return Utils.dashFormatter(val);
          },
        },
        {
          cellClass: ['text-left'],
          cellRenderer: AgIconLinkCellComponent,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              icons: [
                {
                  icon: 'AlertTriangle',
                  iconClass: 'text-aux-red-dark',
                  iconTooltipMessages: [MessagesConstants.PO.STATUSES_DO_NOT_MATCH],
                  showIcon:
                    isWarningsVisible$.value &&
                    !params.node.rowPinned &&
                    params.data.po_status !== params.data.po_report_status,
                },
              ],
              rowReverse: true,
            };
          },
          field: 'po_status',
          filter: true,
          filterParams: {
            showTooltips: true,
            valueFormatter: (params) =>
              MapPurchaseOrderStatus[params.value as keyof typeof MapPurchaseOrderStatus],
          } as ISetFilterParams,
          headerClass: ['ag-header-align-center'],
          headerName: 'Auxilius PO Status',
          menuTabs: ['filterMenuTab'],
          minWidth: 180,
          suppressMenu: false,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) => {
            return params.data?.po_status
              ? MapPurchaseOrderStatus[
                  params.data?.po_status as keyof typeof MapPurchaseOrderStatus
                ]
              : Utils.zeroHyphen;
          },
        },
        {
          cellClass: ['text-left', AuxExcelStyleKeys.BORDER_RIGHT],
          cellRenderer: AgIconLinkCellComponent,
          field: 'po_report_status',
          filter: true,
          filterParams: {
            showTooltips: true,
            valueFormatter: (params) =>
              MapPurchaseOrderStatus[params.value as keyof typeof MapPurchaseOrderStatus],
          } as ISetFilterParams,
          headerClass: ['ag-header-align-center'],
          headerName: 'PO Report / ERP PO Status',
          menuTabs: ['filterMenuTab'],
          minWidth: 180,
          suppressMenu: false,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) => {
            return params.data?.po_report_status
              ? MapPurchaseOrderStatus[
                  params.data?.po_report_status as keyof typeof MapPurchaseOrderStatus
                ]
              : Utils.zeroHyphen;
          },
        },
      ],
      headerClass: ['ag-header-align-center', '!border-r'],
      headerName: 'Purchase Order Info',
    },
    {
      children: [
        {
          cellClass: (params: CellClassParams) => [
            isNumber(params.value) ? 'aux-link' : 'aux-link-zero-hyphen',
            '!block',
            'truncate',
            'text-right',
            '!leading-[40px]',
          ],
          cellRenderer: AgIconLinkCellComponent,
          cellRendererParams: (params: ICellRendererParams) => {
            const iconTooltipMessages: string[] = [];

            if (!hasBudgetEditPermission) {
              iconTooltipMessages.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
            }

            if (isChangeOrdersWorkflowLocked) {
              iconTooltipMessages.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
            }

            if (!params.data.organization_id) {
              iconTooltipMessages.push(MessagesConstants.PO.ADD_VENDOR_FOR_PO);
            }

            return {
              icons: [
                {
                  icon: 'Pencil',
                  iconClass: 'text-aux-blue-dark',
                  iconClick: attributesModalNavigate,
                  iconDisabled: iconTooltipMessages.length,
                  iconTooltipMessages: iconTooltipMessages,
                  showIcon: !params.node.rowPinned,
                },
              ],
              rowReverse: true,
              textClick: budgetNavigate,
              textDisabled: !params.node.rowPinned && !params.data.organization_id,
              textTooltipMessages:
                !params.node.rowPinned && !params.data.organization_id
                  ? [MessagesConstants.PO.ADD_VENDOR_FOR_PO]
                  : [],
            };
          },
          field: 'linkedActivityTotal',
          headerClass: ['ag-header-align-center'],
          headerName: 'Auxilius',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', '!leading-[40px]'],
          cellRenderer: AgTooltipComponent,
          field: 'po_report_amount',
          headerClass: ['ag-header-align-center'],
          headerName: 'PO Report / ERP',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', AuxExcelStyleKeys.BORDER_RIGHT],
          cellRenderer: AgIconLinkCellComponent,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              icons: [
                {
                  icon: 'AlertTriangle',
                  iconClass: 'text-aux-red-dark',
                  iconTooltipMessages: [MessagesConstants.PO.PO_VARIANCE],
                  showIcon:
                    isWarningsVisible$.value &&
                    isNumber(params.value) &&
                    !isRoundedToZero(params.value),
                },
              ],
            };
          },
          field: 'po_amount_variance',
          headerClass: ['ag-header-align-center'],
          headerName: 'Variance ($)',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
      ],
      headerClass: ['ag-header-align-center'],
      headerName: 'Purchase Order Amount',
    },
    {
      children: [
        {
          cellClass: (params: CellClassParams) => [
            isNumber(params.value) ? 'aux-link' : 'aux-link-zero-hyphen',
            '!block',
            'truncate',
            'text-right',
            'cursor-pointer',
            '!leading-[40px]',
          ],
          cellRenderer: AgTooltipComponent,
          field: 'invoiced_against',
          headerClass: ['ag-header-align-center'],
          headerName: 'Auxilius',
          minWidth: 160,
          onCellClicked: (event: CellClickedEvent) => invoicesNavigate(event),
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', '!leading-[40px]'],
          field: 'last_invoice_date',
          headerClass: ['ag-header-align-center'],
          headerName: 'Last Invoice Date',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: Utils.dashFormatter,
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', '!leading-[40px]'],
          cellRenderer: AgTooltipComponent,
          field: 'po_report_invoiced_amount',
          headerClass: ['ag-header-align-center'],
          headerName: 'PO Report / ERP',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', AuxExcelStyleKeys.BORDER_RIGHT],
          cellRenderer: AgIconLinkCellComponent,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              icons: [
                {
                  icon: 'AlertTriangle',
                  iconClass: 'text-aux-red-dark',
                  iconTooltipMessages: [MessagesConstants.PO.INVOICED_VARIANCE],
                  showIcon:
                    isWarningsVisible$.value &&
                    isNumber(params.value) &&
                    !isRoundedToZero(params.value),
                },
              ],
            };
          },
          field: 'invoiced_against_variance',
          headerClass: ['ag-header-align-center'],
          headerName: 'Variance ($)',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
      ],
      headerClass: ['ag-header-align-center'],
      headerName: 'Invoiced Against',
    },
    {
      children: [
        {
          cellClass: ['!block', 'truncate', 'text-right', '!leading-[40px]'],
          cellRenderer: AgTooltipComponent,
          field: 'po_remaining',
          headerClass: ['ag-header-align-center'],
          headerName: 'Auxilius',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', '!leading-[40px]'],
          cellRenderer: AgTooltipComponent,
          field: 'po_remaining_invoiced_amount',
          headerClass: ['ag-header-align-center'],
          headerName: 'PO Report / ERP',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
        {
          cellClass: ['!block', 'truncate', 'text-right', AuxExcelStyleKeys.BORDER_RIGHT],
          cellRenderer: AgIconLinkCellComponent,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              icons: [
                {
                  icon: 'AlertTriangle',
                  iconClass: 'text-aux-red-dark',
                  iconTooltipMessages: [MessagesConstants.PO.REMAINING_VARIANCE],
                  showIcon:
                    isWarningsVisible$.value &&
                    isNumber(params.value) &&
                    !isRoundedToZero(params.value),
                },
              ],
            };
          },
          field: 'po_remaining_variance',
          headerClass: ['ag-header-align-center'],
          headerName: 'Variance ($)',
          minWidth: 160,
          suppressMenu: true,
          suppressMovable: true,
          valueFormatter: (params: ValueFormatterParams) =>
            Utils.currencyFormatter(
              isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
              {},
              params.data.currency,
              true
            ),
        },
      ],
      headerClass: ['ag-header-align-center'],
      headerName: 'Remaining',
    },
    {
      cellClass: ['!block', TableConstants.STYLE_CLASSES.CELL_ALIGN_LEFT],
      cellRenderer: AgTooltipComponent,
      field: 'description',
      headerClass: ['ag-header-align-center'],
      headerName: 'Notes',
      maxWidth: 1100,
      minWidth: 160,
      suppressMenu: true,
      suppressMovable: true,
    },
  ];
};
